import { NewModal } from 'lib/components';
import React from 'react';
import { ILandingPageDesignGroup } from '../types';
import { Flex, Gap } from 'lib/components/styles/layout';
import { Button } from 'react-covideo-common';
import { Form, Formik, FormikValues } from 'formik';
import { FormikInputField } from 'lib/components/formik/FormikInputField';
import { useEditGroupMutation } from 'lib/api/superadmin/landingPageDesignGroups/useEditGroupMutation';
import * as Yup from 'yup';
import { useCreateGroupMutation } from 'lib/api/superadmin/landingPageDesignGroups/useCreateGroupMutation';

type CreateEditGroupProps = {
  group?: ILandingPageDesignGroup;
  onClose: () => void;
};

const validationSchema = Yup.object({
  title: Yup.string()
    .required('Title is required')
    .max(80, 'Title must be at most 80 characters')
    .test(
      'not-just-spaces',
      'Please enter a valid title',
      value => !value || value.trim().length > 0
    ),
});

const CreateEditGroupModal = ({ group, onClose }: CreateEditGroupProps) => {
  const { mutateAsync: createGroup, isLoading: isCreating } =
    useCreateGroupMutation({
      shouldShowNotifications: true,
      onSuccess: onClose,
    });
  const { mutateAsync: editGroup, isLoading: isSaving } = useEditGroupMutation(
    group?.landingPageDesignGroupId || 0,
    onClose
  );

  const isEditMode = !!group;
  const initialFormikValues = {
    title: isEditMode ? group.title : '',
  };

  const onSubmit = async (values: FormikValues) => {
    isEditMode
      ? await editGroup({
          title: values.title.trim(),
        })
      : await createGroup({
          title: values.title.trim(),
          landingPageDesigns: [],
        });
  };

  const isSubmitting = isEditMode ? isSaving : isCreating;

  return (
    <NewModal
      closeModal={onClose}
      headerText={isEditMode ? `Edit "${group.title}"` : 'Add New Group'}
    >
      <Formik
        initialValues={initialFormikValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        validateOnMount
      >
        {({ handleSubmit, isValid, submitForm }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <Flex gap='20px'>
                <FormikInputField
                  name='title'
                  isRequired
                  label='Group Name'
                  disabled={isSubmitting}
                />
                <Gap gap='8px' justifyContent='flex-end'>
                  <Button
                    text='Cancel'
                    onClick={onClose}
                    variant='secondary'
                    disabled={isSubmitting}
                  />
                  <Button
                    text={isSaving ? 'Saving...' : 'Save Changes'}
                    onClick={submitForm}
                    disabled={!isValid || isSubmitting}
                  />
                </Gap>
              </Flex>
            </Form>
          );
        }}
      </Formik>
    </NewModal>
  );
};

export default CreateEditGroupModal;
